import { useState, useEffect, useCallback } from "react";
import { api, formatCurrency, monthNames } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, Plus, Calculator, AlertTriangle, Lock, Unlock, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Section réutilisable pour saisir / éditer l'avancement CA d'une commande
 * par mois. Utilisable soit inline dans le dialog d'édition, soit dans un
 * dialog standalone (voir OrderProgressDialog).
 *
 * Props :
 * - order : { id, reference, amount_ht, status, client_name, ... }
 * - onChange (optionnel) : appelée après chaque save/delete pour rafraîchir
 *   le parent (ex : fetchData).
 */
export const OrderProgressSection = ({ order, onChange }) => {
  const currentYear = new Date().getFullYear();
  const [entries, setEntries] = useState([]); // trié par (year, month)
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allowOver100, setAllowOver100] = useState(false);

  // Formulaire d'ajout
  const [newMonth, setNewMonth] = useState(new Date().getMonth() + 1);
  const [newYear, setNewYear] = useState(currentYear);
  const [newPercent, setNewPercent] = useState("");

  const isCancelled = order?.status === "cancelled";
  const amountHt = order?.amount_ht || 0;

  const fetchProgress = useCallback(async () => {
    if (!order?.id) return;
    try {
      setLoading(true);
      const res = await api.getOrderProgress({ order_id: order.id });
      const sorted = [...(res.data || [])].sort(
        (a, b) => (a.year * 12 + a.month) - (b.year * 12 + b.month)
      );
      setEntries(sorted);
    } catch {
      toast.error("Erreur lors du chargement des avancements");
    } finally {
      setLoading(false);
    }
  }, [order?.id]);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  const totalPercent = entries.reduce((sum, e) => sum + (e.progress_percent || 0), 0);
  const totalCA = entries.reduce((sum, e) => sum + (e.ca_generated || 0), 0);
  const remaining = Math.max(0, 100 - totalPercent);

  const years = Array.from({ length: currentYear - 2020 + 3 }, (_, i) => currentYear + 2 - i);

  const handleAdd = async () => {
    const pct = parseFloat(newPercent);
    if (isNaN(pct) || pct <= 0) {
      toast.error("Pourcentage invalide");
      return;
    }
    // Vérifier qu'il n'existe pas déjà une ligne pour ce (year, month)
    const duplicate = entries.find((e) => e.year === newYear && e.month === newMonth);
    if (duplicate) {
      toast.error(`Un avancement existe déjà pour ${monthNames[newMonth - 1]} ${newYear}. Modifiez-le dans la liste.`);
      return;
    }
    // Bloquer >100 % sauf si débloqué
    const futureTotal = totalPercent + pct;
    if (futureTotal > 100 && !allowOver100) {
      toast.error(
        `Cumul ${futureTotal.toFixed(1)}% > 100%. Cochez "Autoriser >100%" pour débloquer (avenant).`
      );
      return;
    }
    try {
      setSaving(true);
      await api.createOrderProgress({
        order_id: order.id,
        year: newYear,
        month: newMonth,
        progress_percent: pct,
      });
      toast.success(`Avancement ${pct}% enregistré pour ${monthNames[newMonth - 1]} ${newYear}`);
      setNewPercent("");
      await fetchProgress();
      if (onChange) onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur à l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (entry, newVal) => {
    const pct = parseFloat(newVal);
    if (isNaN(pct) || pct < 0) {
      toast.error("Pourcentage invalide");
      return;
    }
    // Cumul sans cette ligne
    const otherTotal = totalPercent - (entry.progress_percent || 0);
    if (otherTotal + pct > 100 && !allowOver100) {
      toast.error(
        `Cumul ${(otherTotal + pct).toFixed(1)}% > 100%. Cochez "Autoriser >100%" pour débloquer.`
      );
      return;
    }
    try {
      setSaving(true);
      await api.createOrderProgress({
        order_id: order.id,
        year: entry.year,
        month: entry.month,
        progress_percent: pct,
      });
      toast.success("Avancement mis à jour");
      await fetchProgress();
      if (onChange) onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur à la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Supprimer l'avancement de ${monthNames[entry.month - 1]} ${entry.year} ?`)) return;
    try {
      setSaving(true);
      await api.deleteOrderProgress(entry.id);
      toast.success("Avancement supprimé");
      await fetchProgress();
      if (onChange) onChange();
    } catch {
      toast.error("Erreur à la suppression");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 border-t pt-4" data-testid="order-progress-section">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Calculator className="h-5 w-5 text-primary" />
          <h3 className="font-heading font-medium">Avancement CA mensuel</h3>
        </div>
        <div className="text-right">
          <div className="text-xs text-muted-foreground">Cumul enregistré</div>
          <div className="text-sm font-bold">
            <span className={totalPercent > 100 ? "text-orange-600" : "text-primary"}>
              {totalPercent.toFixed(1)}%
            </span>
            <span className="text-muted-foreground mx-1">•</span>
            <span className="currency">{formatCurrency(totalCA)}</span>
          </div>
        </div>
      </div>

      {isCancelled && (
        <div className="flex items-start gap-2 text-xs bg-orange-50 border border-orange-200 rounded-sm p-2">
          <AlertTriangle className="h-4 w-4 text-orange-600 shrink-0 mt-0.5" />
          <div className="text-orange-800">
            Commande annulée — historique figé. Impossible d'ajouter un nouvel avancement
            (vous pouvez toujours modifier ou supprimer les lignes existantes).
          </div>
        </div>
      )}

      {/* Liste des avancements */}
      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : entries.length === 0 ? (
        <div className="text-sm text-muted-foreground italic text-center py-3">
          Aucun avancement saisi pour cette commande.
        </div>
      ) : (
        <div className="border rounded-sm overflow-hidden">
          <table className="data-table text-sm">
            <thead>
              <tr>
                <th>Période</th>
                <th className="w-28">%</th>
                <th>CA généré</th>
                <th className="w-16"></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} data-testid={`progress-row-${entry.year}-${entry.month}`}>
                  <td className="font-medium">
                    {monthNames[entry.month - 1]} {entry.year}
                  </td>
                  <td>
                    <ProgressInput
                      value={entry.progress_percent}
                      onSave={(v) => handleUpdate(entry, v)}
                      disabled={saving}
                      testid={`progress-input-${entry.year}-${entry.month}`}
                    />
                  </td>
                  <td className="currency text-primary font-medium">
                    {formatCurrency(amountHt * (entry.progress_percent || 0) / 100)}
                  </td>
                  <td className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(entry)}
                      disabled={saving}
                      data-testid={`progress-delete-${entry.year}-${entry.month}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Formulaire d'ajout */}
      {!isCancelled && (
        <div className="space-y-3 bg-slate-50 p-3 rounded-sm border">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Ajouter un avancement</Label>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <Checkbox
                checked={allowOver100}
                onCheckedChange={(c) => setAllowOver100(!!c)}
                data-testid="allow-over-100-checkbox"
              />
              <span className="flex items-center gap-1">
                {allowOver100 ? <Unlock className="h-3 w-3 text-orange-600" /> : <Lock className="h-3 w-3" />}
                Autoriser &gt; 100% (avenant)
              </span>
            </label>
          </div>
          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-4">
              <Select value={String(newMonth)} onValueChange={(v) => setNewMonth(Number(v))}>
                <SelectTrigger data-testid="progress-new-month"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {monthNames.map((n, i) => (
                    <SelectItem key={i} value={String(i + 1)}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-3">
              <Select value={String(newYear)} onValueChange={(v) => setNewYear(Number(v))}>
                <SelectTrigger data-testid="progress-new-year"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-3 relative">
              <Input
                type="number"
                min="0"
                max={allowOver100 ? undefined : 100}
                step="0.5"
                placeholder={`Max ${remaining.toFixed(0)}%`}
                value={newPercent}
                onChange={(e) => setNewPercent(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                data-testid="progress-new-percent"
              />
            </div>
            <div className="col-span-2">
              <Button
                type="button"
                onClick={handleAdd}
                disabled={saving || !newPercent}
                className="w-full"
                data-testid="progress-add-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          {newPercent && !isNaN(parseFloat(newPercent)) && (
            <div className="text-xs text-muted-foreground">
              CA prévu : <strong className="text-primary currency">
                {formatCurrency(amountHt * parseFloat(newPercent) / 100)}
              </strong> — cumul futur : <strong className={(totalPercent + parseFloat(newPercent)) > 100 ? "text-orange-600" : ""}>
                {(totalPercent + parseFloat(newPercent)).toFixed(1)}%
              </strong>
            </div>
          )}
        </div>
      )}

      {remaining === 0 && !isCancelled && totalPercent <= 100 && (
        <Badge className="bg-blue-100 text-blue-800">Commande à 100%</Badge>
      )}
    </div>
  );
};

/**
 * Petite cellule d'édition du pourcentage : affiche la valeur actuelle,
 * sauvegarde sur blur ou touche Enter.
 */
const ProgressInput = ({ value, onSave, disabled, testid }) => {
  const [val, setVal] = useState(String(value ?? 0));

  useEffect(() => {
    setVal(String(value ?? 0));
  }, [value]);

  const commit = () => {
    if (parseFloat(val) !== parseFloat(value)) {
      onSave(val);
    }
  };

  return (
    <Input
      type="number"
      min="0"
      step="0.5"
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
      disabled={disabled}
      className="h-8 w-20 text-sm"
      data-testid={testid}
    />
  );
};

export default OrderProgressSection;
